/**
 * Represents a feature hierarchy in the application.
 *
 * @interface Feature
 */
export interface Feature {
    /**
     * Unique identifier for the feature.
     */
    id: string;

    /**
     * Display name of the feature.
     */
    name: string;

    /**
     * UI-related metadata for the feature.
     */
    ui: {
        /**
         * Hierarchy information for menus.
         */
        hierarchy: {
            /**
             * Name of the main menu this feature belongs to.
             */
            mainMenu?: string;

            /**
             * Name of the submenu this feature belongs to.
             */
            subMenu?: string;

            /**
             * Level in the menu hierarchy.
             */
            level?: number;

            /**
             * Order in the menu or submenu.
             */
            order?: number;
        };

        /**
         * Icon information for different UI contexts.
         */
        icons: {
            /**
             * Icon used in menus.
             */
            menu?: string;

            /**
             * Icon used for group representation.
             */
            group?: string;

            /**
             * Main icon for the feature.
             */
            main?: string;
        };

        /**
         * Routing information for the feature.
         */
        route: {
            /**
             * Path for the route.
             */
            path: string;

            /**
             * List of route parameters.
             */
            params: string[];

            /**
             * List of query parameters for the route.
             */
            queryParams: string[];
        };
    };
}


/**
 * Enum representing the different roles in the application.
 *
 * @enum {string}
 */
export enum Roles {

    /**
     * Employee administrator.
     */
    EMPLOYEE = "EMPLOYEE",

    /**
     * Farm Business administrator with full access to business operations.
     */
    FARM_BUSINESS_ADMIN = "Farm Business Admin",

    /**
     * Owner of a farm.
     */
    FARMER = "Farmer",
}

/**
 * Maps each role to the list of features accessible by that role.
 *
 * @type {Record<Roles, Feature[]>}
 */
export const roleToFeatures: Record<Roles, Feature[]> = {
    [Roles.EMPLOYEE]: [
        {
            "id": "DEVICE_MODELS",
            "name": "Models",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/deviceModels",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DEVICE_PARAMETERS",
            "name": "Params",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/deviceParameters",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DEVICES",
            "name": "Devices",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/devices",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ASSIGN_DEVICE",
            "name": "Assignments",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/assignDevice",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DEVICE_HEALTH_MONITORING",
            "name": "Monitor",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/deviceHealthMonitoring",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DEVICE_ONHAND",
            "name": "Inventory",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/deviceOnHand",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DEVICE_TRACKING",
            "name": "Tracking",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/deviceTracking",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "AUTO_UPDATE",
            "name": "Auto Update",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/autoUpdate",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FILE_DOWNLOAD",
            "name": "Downloads",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/fileDownload",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FILE_UPLOAD",
            "name": "Uploads",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/fileUpload",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "IO_PORTS",
            "name": "I/O Ports",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/ioPorts",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WIFI",
            "name": "Wi-Fi",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Devices",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/wifi",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SENSORS",
            "name": "Sensors",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Sensors",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/sensors",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SENSOR_TYPES",
            "name": "Types",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Sensors",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/sensorTypes",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SR_TOPICS",
            "name": "Topics",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Requests",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/srTopics",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SR_STATUS_CODES",
            "name": "Statuses",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Requests",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/srStatusCodes",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SERVICE_REQUESTS",
            "name": "Requests",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Requests",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/serviceRequests",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "QUALITY_PARAMETER_RANGES",
            "name": "Ranges",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Parameters",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/qualityParameterRanges",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "QUALITY_PARAMETER_TYPES",
            "name": "Types",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Parameters",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/qualityParameterTypes",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "QUALITY_PARAMETERS",
            "name": "Parameters",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Parameters",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/qualityParameters",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TEST_CATEGORIES",
            "name": "Test Categories",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Parameters",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/testCategories",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "CONTINUOUS_MONITORING",
            "name": "CM Live",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Monitoring",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/continuousMonitoring",
                    "params": [],
                    "queryParams": []
                }
            }
        },
    ],
    [Roles.FARMER]: [
        {
            "id": "DASHBOARD",
            "name": "Dashboard",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 0
                },
                "icons": {
                    "main": "Dashboard.svg"
                },
                "route": {
                    "path": "/home",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WATER_QUALITY",
            "name": "Water Quality",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": null,
                    "level": 1,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmwaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
        "id": "YOUR_BUSINESS",
        "name": "Your Business",
        "ui": {
            "hierarchy": {
                "mainMenu": "Manage",
                "subMenu": null,
                "level": 1,
                "order": 2
            },
            "icons": {
                "menu": "manage-icon.svg",
            },
            "route": {
                "path": "/home/locations",
                "params": [],
                "queryParams": []
            }
        }
    },
        {
            "id": "DEVICE_MAPPING",
            "name": "Devices",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                },
                "route": {
                    "path": "/home/devicemapping",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BARCODES",
            "name": "Barcodes",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                },
                "route": {
                    "path": "/home/addbarcode",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SECTIONS",
            "name": "Sections",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                },
                "route": {
                    "path": "/home/sections",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PONDS",
            "name": "Ponds",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                },
                "route": {
                    "path": "/home/ponds",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND_CULTURES",
            "name": "Pond Cultures",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                },
                "route": {
                    "path": "/home/pondcultures",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BATCH_TRANSACTIONS",
            "name": "Batch Water Readings",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/batchentrydashboard",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ANALYTICS",
            "name": "Analytics",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Charts",
                    "subMenu": null,
                    "level": 1,
                    "order": 3
                },
                "icons": {
                    "menu": "Charts.svg",
                },
                "route": {
                    "path": "/home/analytics",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRACEABILITY",
            "name": "Traceability",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": null,
                    "level": 1,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/traceabilityreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "READINGS_HISTORY",
            "name": "Water Quality Readings",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": null,
                    "level": 1,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/historywaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
    ],
    [Roles.FARM_BUSINESS_ADMIN]: [
        {
            "id": "DASHBOARD",
            "name": "Dashboard",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 0
                },
                "icons": {
                    "main": "Dashboard.svg"
                },
                "route": {
                    "path": "/home",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WATER_QUALITY",
            "name": "Water Quality",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": null,
                    "level": 1,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmwaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
        "id": "YOUR_BUSINESS",
        "name": "Your Business",
        "ui": {
            "hierarchy": {
                "mainMenu": "Manage",
                "subMenu": null,
                "level": 1,
                "order": 2
            },
            "icons": {
                "menu": "manage-icon.svg",
            },
            "route": {
                "path": "/home/locations",
                "params": [],
                "queryParams": []
            }
        }
    },
        {
            "id": "DEVICE_MAPPING",
            "name": "Devices",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                },
                "route": {
                    "path": "/home/devicemapping",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BARCODES",
            "name": "Barcodes",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                },
                "route": {
                    "path": "/home/addbarcode",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SECTIONS",
            "name": "Sections",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                },
                "route": {
                    "path": "/home/sections",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PONDS",
            "name": "Ponds",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                },
                "route": {
                    "path": "/home/ponds",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND_CULTURES",
            "name": "Pond Cultures",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                },
                "route": {
                    "path": "/home/pondcultures",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BATCH_TRANSACTIONS",
            "name": "Batch Water Readings",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/batchentrydashboard",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ANALYTICS",
            "name": "Analytics",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Charts",
                    "subMenu": null,
                    "level": 1,
                    "order": 3
                },
                "icons": {
                    "menu": "Charts.svg",
                },
                "route": {
                    "path": "/home/analytics",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRACEABILITY",
            "name": "Traceability",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": null,
                    "level": 1,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/traceabilityreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "READINGS_HISTORY",
            "name": "Water Quality Readings",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": null,
                    "level": 1,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/historywaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
    ],
}

/**
 * Returns the list of features available for a given role.
 *
 * @param role - The role whose features you want to fetch
 * @returns An array of Feature objects (empty array if role is not found)
 */
export function getFeaturesWithHierarchyByRole(role: Roles): Feature[] {
    return roleToFeatures[role] ?? [];
}
